import React from "react";

import { FormControl, AppSettings, Grid } from "lib/index";
import createCompanySettingsView from "modules/settings/createCompanySettingsView";
import { stateSelector, initializeView, updateModel, submit } from "./index";
import Hint from "./CompanySettingsHint";

const FormContent = ({ company, errors, handleCompanyPropChange, canEdit }) => (
  <div>
    <FormControl.InputFormGroup
      {...FormControl.addProps(`model.name`, { errors })}
      value={company.name}
      onChange={handleCompanyPropChange.bind(null, "name")}
      readOnly={true}
      label={I18n.humanAttributeName("company", "name")}
      required
    />

    <FormControl.ImageAttachmentFormGroup
      {...FormControl.addProps(`model.logo_image`, { errors })}
      value={company.logo_image}
      onChange={handleCompanyPropChange.bind(null, "logo_image")}
      readOnly={!canEdit}
      label={I18n.humanAttributeName("company", "logo_image")}
    />
    <div>
      <FormControl.LocationFormGroup
        {...FormControl.addProps(`${name}.location`, { errors })}
        label={I18n.humanAttributeName("product_pickup_point", "location")}
        value={{
          name: company.location_name,
          lat: company.location_lat,
          lng: company.location_lng,
        }}
        onBlur={null}
        onChange={handleCompanyPropChange.bind(null, "location")}
      />
    </div>
    <div className="ui section">
      <h3>{I18n.t("papp.settings.CompanySettingsView.contact_details")}</h3>
      <Grid columns={2}>
        <Grid.Column>
          <FormControl.InputFormGroup
            {...FormControl.addProps(`model.contact_email`, { errors })}
            value={company.contact_email}
            onChange={handleCompanyPropChange.bind(null, "contact_email")}
            readOnly={!canEdit}
            label={I18n.humanAttributeName("company", "email")}
            placeholder={I18n.humanAttributeName("company", "contact_email_placeholder")}
            type="email"
          />
        </Grid.Column>
        <Grid.Column>
          <FormControl.InputFormGroup
            {...FormControl.addProps(`model.contact_phone_number`, { errors })}
            value={company.contact_phone_number}
            onChange={handleCompanyPropChange.bind(null, "contact_phone_number")}
            readOnly={!canEdit}
            label={I18n.humanAttributeName("company", "phone_number")}
            placeholder={I18n.t("papp.settings.CompanySettingsView.contact_phone_number_placeholder")}
            type="tel"
          />
        </Grid.Column>
      </Grid>
      <Grid columns={2}>
        <Grid.Column>
          <FormControl.TextAreaFormGroup
            {...FormControl.addProps(`model.contact_full_address`, { errors })}
            value={company.contact_full_address}
            onChange={handleCompanyPropChange.bind(null, "contact_full_address")}
            readOnly={!canEdit}
            label={I18n.humanAttributeName("company", "contact_full_address")}
            placeholder={I18n.humanAttributeName("company", "contact_full_address_placeholder")}
            autoHeight
          />
        </Grid.Column>
        <Grid.Column>
          <FormControl.TextAreaFormGroup
            {...FormControl.addProps(`model.contact_opening_hours`, { errors })}
            value={company.contact_opening_hours}
            onChange={handleCompanyPropChange.bind(null, "contact_opening_hours")}
            readOnly={!canEdit}
            label={I18n.humanAttributeName("company", "contact_opening_hours")}
            placeholder={I18n.humanAttributeName("company", "contact_opening_hours_placeholder")}
            autoHeight
          />
        </Grid.Column>
      </Grid>
      <Grid columns={2} className="u-marginBottom">
        <Grid.Column>
          <FormControl.InputFormGroup
            {...FormControl.addProps(`model.contact_website_url`, { errors })}
            value={company.contact_website_url}
            onChange={handleCompanyPropChange.bind(null, "contact_website_url")}
            readOnly={!canEdit}
            label={I18n.humanAttributeName("company", "contact_website_url")}
            placeholder={I18n.humanAttributeName("company", "contact_website_url_placeholder")}
            type="url"
          />
        </Grid.Column>
        <Grid.Column>
          <FormControl.InputFormGroup
            {...FormControl.addProps(`model.contact_facebook_url`, { errors })}
            value={company.contact_facebook_url}
            onChange={handleCompanyPropChange.bind(null, "contact_facebook_url")}
            readOnly={!canEdit}
            label={"Facebook"}
            placeholder={I18n.humanAttributeName("company", "contact_facebook_url_placeholder")}
            type="url"
          />
        </Grid.Column>
      </Grid>
      <FormControl.LocaleSelectFormGroup
        {...FormControl.addProps(`model.contact_languages`, { errors })}
        value={company.contact_languages || []}
        onChange={handleCompanyPropChange.bind(null, "contact_languages")}
        readOnly={!canEdit}
        label={I18n.humanAttributeName("company", "contact_languages")}
        placeholder={I18n.humanAttributeName("company", "contact_languages_placeholder")}
        multiple
        search
      />
    </div>

    {(company.supplier || company.agent) && (
      <div className="ui section">
        <h3>{I18n.t("papp.settings.CompanySettingsView.notifications_details")}</h3>
        <Grid columns={2} className="u-marginBottom">
          <Grid.Column>
            <FormControl.InputFormGroup
              {...FormControl.addProps(`model.notifications_email`, { errors })}
              value={company.notifications_email}
              onChange={handleCompanyPropChange.bind(null, "notifications_email")}
              readOnly={!canEdit}
              label={I18n.humanAttributeName("company", "email")}
              placeholder={I18n.t("papp.settings.CompanySettingsView.notifications_email_placeholder", {
                default_value: company.contact_email || I18n.t("papp.common.none"),
              })}
              type="email"
              help={I18n.t("papp.settings.CompanySettingsView.notifications_email_label")}
            />
          </Grid.Column>
          <Grid.Column>
            <FormControl.InputFormGroup
              {...FormControl.addProps(`model.notifications_phone_number`, {
                errors,
              })}
              value={company.notifications_phone_number}
              onChange={handleCompanyPropChange.bind(null, "notifications_phone_number")}
              readOnly={!canEdit}
              label={I18n.humanAttributeName("company", "phone_number")}
              placeholder={I18n.t("papp.settings.CompanySettingsView.notifications_phone_number_placeholder", {
                default_value: company.contact_phone_number || I18n.t("papp.common.none"),
              })}
              type="tel"
              help={I18n.t("papp.settings.CompanySettingsView.notifications_phone_number_label")}
            />
          </Grid.Column>
        </Grid>
      </div>
    )}

    <div className="ui section">
      <h3>{I18n.t("papp.settings.CompanySettingsView.other_details")}</h3>
      <Grid columns={2}>
        <Grid.Column>
          <FormControl.LocaleSelectFormGroup
            {...FormControl.addProps(`model.locale`, { errors })}
            value={company.locale}
            onChange={handleCompanyPropChange.bind(null, "locale")}
            readOnly={!canEdit}
            label={I18n.humanAttributeName("company", "locale")}
            locales="company_locales"
            search
          />
        </Grid.Column>
        <Grid.Column>
          <FormControl.SelectFormGroup
            {...FormControl.addProps(`model.time_zone_name`, { errors })}
            value={company.time_zone_name}
            onChange={handleCompanyPropChange.bind(null, "time_zone_name")}
            readOnly={!canEdit}
            label={I18n.humanAttributeName("company", "time_zone_name")}
            options={AppSettings.time_zones}
          />
        </Grid.Column>
      </Grid>
    </div>
  </div>
);

const CompanySettingsView = createCompanySettingsView({
  stateSelector,
  actions: { initializeView, updateModel, submit },
  title: I18n.t("papp.settings.CompanySettingsView.title"),
  FormContent,
  Hint,
});

export default CompanySettingsView;



// WEBPACK FOOTER //
// ./src/modules/settings/CompanySettingsView/CompanySettingsView.jsx